import React, { useEffect, useMemo, useState } from 'react';
import { Card, DataTable } from '../../../components/ui';
import { DynamicColumn, DynamicListTable } from '../../../components/DynamicListTable';
import { NumerosGrid, TipoNumerosGrid } from '../../../components/NumerosGrid';
import { fetchAPI } from '../../../services/apiClient';
import { fechaLocalYMD } from '../../../utils/date';
import { Printer } from 'lucide-react';
import { isNativePrintSelected, printRawByDestination } from '../../../utils/printer';
import { useAuth } from '../../../app/auth';
import { generarPdfListaBlob } from '../../../utils/pdfListas';
import { shareImageNative } from '../../../utils/nativeShare';
import { openWhatsApp } from '../../../utils/whatsappShare';

interface SorteoItem {
  id: string;
  tipo_id: string;
  nombre: string;
  fecha: string;
  estado: string;
  jugadas_hoy?: number;
}

interface LocalItem {
  id: string;
  nombre: string;
}

interface PaqueteResumen {
  paquete_id: string;
  paquete_nombre: string;
  local_ids: string[];
}

interface ResumenLocalRow {
  local_id: string;
  local_nombre: string;
  total_jugadas: number;
  total_vendedores: number;
  total_monto: number;
}

const monto = (value: unknown): string => {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return '0';
  return Math.trunc(n).toLocaleString('es-CR');
};

const mapMontos = (rows: any[], keys: string[]): any[] =>
  rows.map((row, index) => {
    const out = { ...row, id: row.id ?? index + 1 };
    for (const key of keys) {
      if (key in out) out[key] = monto(out[key]);
    }
    return out;
  });



const columnsByTipo = (tipo: string): DynamicColumn[] => {
  switch (tipo) {
    case 'NORMAL':
      return [
        { key: 'numero', label: '#', type: 'readonly', width: '80px' },
        { key: 'monto_vendedor', label: 'VENDEDOR', type: 'readonly', width: '140px' },
        { key: 'monto_local', label: 'LOCAL', type: 'readonly', width: '140px' },
        { key: 'monto_banca', label: 'BANCA', type: 'readonly', width: '140px' },
        { key: 'monto_total', label: 'TOTAL', type: 'readonly', width: '140px' },
      ];
    case 'REVENTADO':
      return [
        { key: 'numero', label: '#', type: 'readonly', width: '120px' },
        { key: 'vendido_rev1', label: 'REV1', type: 'readonly', width: '160px' },
        { key: 'vendido_rev2', label: 'REV2', type: 'readonly', width: '160px' },
      ];
    case 'MEGA':
      return [
        { key: 'numero', label: '#', type: 'readonly', width: '100px' },
        { key: 'rev1', label: 'REV1', type: 'readonly', width: '130px' },
        { key: 'rev2', label: 'REV2', type: 'readonly', width: '130px' },
        { key: 'num2', label: 'NÚM2', type: 'readonly', width: '80px' },
        { key: 'monto_mega', label: 'MONTO', type: 'readonly', width: '140px' },
      ];
    case 'MONAZOS':
      return [
        { key: 'numero', label: 'NUM', type: 'readonly', width: '120px' },
        { key: 'tipo', label: 'TIPO', type: 'readonly', width: '120px' },
        { key: 'vendido', label: 'MONTO', type: 'readonly', width: '180px' },
      ];
    case 'FECHAS':
      return [
        { key: 'numero', label: 'FECHA', type: 'readonly', width: '100px' },
        { key: 'monto_vendedor', label: 'VENDEDOR', type: 'readonly', width: '140px' },
        { key: 'monto_local', label: 'LOCAL', type: 'readonly', width: '140px' },
        { key: 'monto_banca', label: 'BANCA', type: 'readonly', width: '140px' },
        { key: 'monto_total', label: 'TOTAL', type: 'readonly', width: '140px' },
      ];
    case 'PARLEY':
      return [
        { key: 'num1', label: 'NUM1', type: 'readonly', width: '120px' },
        { key: 'num2', label: 'NUM2', type: 'readonly', width: '120px' },
        { key: 'vendido', label: 'MONTO', type: 'readonly', width: '180px' },
      ];
    default:
      return [];
  }
};

const montoKeysByTipo: Record<string, string[]> = {
  NORMAL: ['monto_vendedor', 'monto_local', 'monto_banca', 'monto_total'],
  REVENTADO: ['vendido_rev1', 'vendido_rev2'],
  MEGA: ['rev1', 'rev2', 'monto_mega'],
  MONAZOS: ['vendido'],
  FECHAS: ['monto_vendedor', 'monto_local', 'monto_banca', 'monto_total'],
  PARLEY: ['vendido'],
};

export const ListasPage: React.FC = () => {
  const { user } = useAuth();
  const esBanca = user?.role === 'BANCA';
  const [sorteos, setSorteos] = useState<SorteoItem[]>([]);
  const [locales, setLocales] = useState<LocalItem[]>([]);
  const [selectedSorteoId, setSelectedSorteoId] = useState('');
  const [selectedLocalId, setSelectedLocalId] = useState('');
  const [fechaFiltro, setFechaFiltro] = useState(fechaLocalYMD);
  const [rows, setRows] = useState<any[]>([]);
  const [resumenLocales, setResumenLocales] = useState<ResumenLocalRow[]>([]);
  const [loadingRows, setLoadingRows] = useState(false);
  const [loadingResumen, setLoadingResumen] = useState(false);
  const [paquetes, setPaquetes] = useState<PaqueteResumen[]>([]);
  const [selectedPaqueteId, setSelectedPaqueteId] = useState('');
  const [enviandoWa, setEnviandoWa] = useState(false);

  // Workflow: listas de locales pendientes de oficializar
  interface ListaLocalPendiente {
    id: string;
    local_id: string;
    local_nombre: string;
    estado: string;
    enviada_banca_at: string | null;
    jugadas_count: number;
  }
  const [pendientes, setPendientes] = useState<ListaLocalPendiente[]>([]);
  const [loadingOficializar, setLoadingOficializar] = useState(false);
  const [msgOficializar, setMsgOficializar] = useState('');

  const selectedSorteo = useMemo(
    () => sorteos.find((s) => s.id === selectedSorteoId) || null,
    [sorteos, selectedSorteoId],
  );

  useEffect(() => {
    Promise.all([
      fetchAPI<SorteoItem[]>(`/sorteos?fecha=${fechaFiltro}`),
      fetchAPI<LocalItem[]>('/listas/locales'),
      fetchAPI<PaqueteResumen[]>('/paquetes-reglas/resumen-banquero'),
    ])
      .then(([sorteosData, localesData, paquetesData]) => {
        setSorteos(sorteosData);
        setLocales(localesData);
        setPaquetes(paquetesData || []);
        setSelectedSorteoId((prev) => {
          if (!sorteosData.length) return '';
          const match = sorteosData.find((s) => s.id === prev);
          if (match) return prev;
          // Preferir sorteo con más jugadas hoy; si ninguno tiene, usar el primero
          const conData = [...sorteosData].sort((a, b) => (b.jugadas_hoy ?? 0) - (a.jugadas_hoy ?? 0));
          return (conData[0]?.jugadas_hoy ?? 0) > 0 ? conData[0].id : sorteosData[0].id;
        });
      })
      .catch((err) => console.error('Error cargando listas banca:', err));
  }, [fechaFiltro]);

  useEffect(() => {
    if (!selectedSorteoId) return;
    setLoadingRows(true);
    const tipo = selectedSorteo?.tipo_id || '';
    const params = new URLSearchParams();
    if (selectedLocalId) params.append('local_id', selectedLocalId);
    params.append('fecha', fechaFiltro);

    let listaPath: string;
    if (selectedPaqueteId) {
      params.append('paquete_id', selectedPaqueteId);
      listaPath = `/listas/consolidada-paquete/${selectedSorteoId}?${params.toString()}`;
    } else {
      const suffix = `?${params.toString()}`;
      listaPath = tipo === 'MEGA'
        ? `/listas/individuales/${selectedSorteoId}${suffix}`
        : `/listas/consolidada/${selectedSorteoId}${suffix}`;
    }

    fetchAPI<any[]>(listaPath)
      .then((data) => {
        if (tipo === 'NORMAL') {
          setRows((data || []).map((r: any) => ({
            numero: String(r.numero).padStart(2, '0'),
            vendido: Number(r.monto_total) || 0,
          })));
        } else {
          setRows(data || []);
        }
      })
      .catch((err) => {
        console.error('Error cargando consolidado:', err);
        setRows([]);
      })
      .finally(() => setLoadingRows(false));
  }, [selectedSorteoId, selectedLocalId, selectedPaqueteId, selectedSorteo?.tipo_id, fechaFiltro]);

  useEffect(() => {
    if (!selectedSorteoId) return;
    setLoadingResumen(true);
    fetchAPI<ResumenLocalRow[]>(`/listas/resumen-locales/${selectedSorteoId}?fecha=${fechaFiltro}`)
      .then((data) => setResumenLocales(data || []))
      .catch((err) => {
        console.error('Error cargando resumen locales:', err);
        setResumenLocales([]);
      })
      .finally(() => setLoadingResumen(false));
  }, [selectedSorteoId, fechaFiltro]);

  // Filas formateadas para DynamicListTable (tipos no NORMAL/REVENTADO/MEGA)
  const formattedRows = useMemo(() => {
    const tipo = selectedSorteo?.tipo_id || '';
    if (tipo === 'REVENTADO') return rows;
    return mapMontos(rows, montoKeysByTipo[tipo] || ['vendido']);
  }, [rows, selectedSorteo?.tipo_id]);

  // Totales consolidados para NORMAL y FECHAS (5 columnas)
  const totalesConsolidado = useMemo(() => {
    const tipo = selectedSorteo?.tipo_id || '';
    if (!['NORMAL', 'FECHAS'].includes(tipo) || rows.length === 0) return null;
    return {
      monto_vendedor: rows.reduce((s, r) => s + (r.monto_vendedor || 0), 0),
      monto_local: rows.reduce((s, r) => s + (r.monto_local || 0), 0),
      monto_banca: rows.reduce((s, r) => s + (r.monto_banca || 0), 0),
      monto_total: rows.reduce((s, r) => s + (r.monto_total || 0), 0),
    };
  }, [rows, selectedSorteo?.tipo_id]);

  const totalJugadas = resumenLocales.reduce((sum, row) => sum + (row.total_jugadas || 0), 0);
  const totalVendedores = resumenLocales.reduce((sum, row) => sum + (row.total_vendedores || 0), 0);
  const totalMonto = resumenLocales.reduce((sum, row) => sum + (row.total_monto || 0), 0);

  // Cargar listas pendientes de oficializar cuando cambia el sorteo
  const cargarPendientes = (sorteoId: string) => {
    fetchAPI<ListaLocalPendiente[]>(`/listas/banca/pendientes?sorteo_id=${sorteoId}`)
      .then(data => setPendientes(data || []))
      .catch(() => setPendientes([]));
  };

  useEffect(() => {
    if (!selectedSorteoId) { setPendientes([]); return; }
    cargarPendientes(selectedSorteoId);
  }, [selectedSorteoId, fechaFiltro]);

  const handleOficializar = async () => {
    if (!pendientes.length || loadingOficializar) return;
    setLoadingOficializar(true);
    setMsgOficializar('');
    try {
      await fetchAPI('/listas/banca/oficializar', {
        method: 'POST',
        body: JSON.stringify({ lista_local_id: pendientes[0].id }),
      });
      setMsgOficializar(`${pendientes.length} lista(s) oficializada(s) correctamente.`);
      setPendientes([]);
    } catch (err: any) {
      setMsgOficializar(err?.message || 'Error al oficializar');
    } finally {
      setLoadingOficializar(false);
    }
  };

  const handleEnviarWa = async () => {
    if (!selectedSorteo || rows.length === 0 || enviandoWa) return;
    setEnviandoWa(true);
    try {
      const paq = paquetes.find((p) => p.paquete_id === selectedPaqueteId);
      const blob = await generarPdfListaBlob({
        sorteo: selectedSorteo,
        fecha: fechaFiltro,
        paqueteNombre: paq ? paq.paquete_nombre : null,
        localNombre: selectedLocalId
          ? locales.find((l) => l.id === selectedLocalId)?.nombre || null
          : null,
        tipo: selectedSorteo.tipo_id || 'NORMAL',
        rows,
      });
      const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      const safeName = selectedSorteo.nombre.replace(/[^a-zA-Z0-9_-]/g, '_');
      const fileName = `lista-${safeName}-${fechaFiltro}-${ts}.pdf`;
      const paqNombre = paq ? paq.paquete_nombre : 'General';
      const texto = `Lista del sorteo ${selectedSorteo.nombre} - ${fechaFiltro} - Paquete: ${paqNombre}`;
      const shared = await shareImageNative(blob, fileName, texto);
      if (!shared) {
        openWhatsApp(texto);
      }
    } catch (err) {
      console.error('Error enviando por WhatsApp:', err);
    } finally {
      setEnviandoWa(false);
    }
  };

  const handlePrint = async () => {
    const tipo = selectedSorteo?.tipo_id || 'NORMAL';
    const cols = columnsByTipo(tipo);
    const printRows = formattedRows;
    const localNombre = selectedLocalId
      ? locales.find((l) => l.id === selectedLocalId)?.nombre || selectedLocalId
      : 'Todos los locales';

    const isSunmi = isNativePrintSelected();
    console.log('[PRINT Listas] ─────────────────────────────────');
    console.log('[PRINT Listas] Sorteo:', selectedSorteo?.nombre);
    console.log('[PRINT Listas] isNativePrintSelected:', isSunmi);
    console.log('[PRINT Listas] Filas:', printRows.length);

    // APK nativa: convertir a texto plano para impresora térmica
    if (isSunmi) {
      try {
        console.log('[PRINT Listas] Ruta nativa seleccionada');
        const lines: string[] = [];
        const hr = '================================';
        lines.push('');
        lines.push(hr);
        lines.push(`LISTA ${selectedSorteo?.nombre || ''}`);
        lines.push(`Tipo: ${tipo}`);
        lines.push(hr);
        lines.push(`Fecha: ${fechaFiltro}`);
        lines.push(`Vista: ${localNombre}`);
        lines.push(`Jugadas: ${printRows.length}`);
        lines.push(hr);
        lines.push('');

        // Header row
        if (tipo === 'NORMAL') {
          lines.push('NUM  VEND  LOC   BANCA TOTAL');
          lines.push('--------------------------------');
          for (const row of printRows) {
            const num = String(row.numero ?? '').padEnd(4).slice(0, 4);
            const vend = String(row.monto_vendedor ?? '').padStart(5).slice(-5);
            const loc = String(row.monto_local ?? '').padStart(5).slice(-5);
            const banca = String(row.monto_banca ?? '').padStart(5).slice(-5);
            const total = String(row.monto_total ?? '').padStart(5).slice(-5);
            lines.push(`${num} ${vend} ${loc} ${banca} ${total}`);
          }
        } else {
          // Simplified output for other types
          lines.push(cols.map(c => c.label.slice(0, 6).padEnd(6)).join(' '));
          lines.push('--------------------------------');
          for (const row of printRows) {
            const vals = cols.map(c => String(row[c.key] ?? '').slice(0, 6).padEnd(6));
            lines.push(vals.join(' '));
          }
        }

        // Totals
        if (totalesConsolidado) {
          lines.push('');
          lines.push(hr);
          lines.push(`TOTAL VEND: C${monto(totalesConsolidado.monto_vendedor)}`);
          lines.push(`TOTAL LOC:  C${monto(totalesConsolidado.monto_local)}`);
          lines.push(`TOTAL BANCA:C${monto(totalesConsolidado.monto_banca)}`);
          lines.push(`TOTAL:      C${monto(totalesConsolidado.monto_total)}`);
        }
        lines.push(hr);
        lines.push('\n\n');

        const text = lines.join('\n');
        console.log('[PRINT Listas] [WORKING PATH] Texto:', text.length, 'chars');
        const result = await printRawByDestination(text, 'BANCA LISTAS');
        if (!result.ok) throw new Error(result.error || 'Impresion falló');
        console.log('[PRINT Listas] ✓ impresion exitosa');
        console.log('[PRINT Listas] ─────────────────────────────────');
        return;
      } catch (printErr: any) {
        console.error('[PRINT Listas] ✗ impresion falló:', printErr?.message);
        console.log('[PRINT Listas] Fallback a web...');
        // Fall through to web printing
      }
    }

    // Web: HTML popup
    console.log('[PRINT Listas] Ruta WEB');
    const thead = `<tr>${cols.map((c) => `<th>${c.label}</th>`).join('')}</tr>`;
    const tbody = printRows
      .map((row) => `<tr>${cols.map((c) => `<td>${row[c.key] ?? ''}</td>`).join('')}</tr>`)
      .join('');

    const resumenRows = resumenLocales
      .map((r) => `<tr><td>${r.local_nombre}</td><td>${r.total_jugadas}</td><td>₡${monto(r.total_monto)}</td></tr>`)
      .join('');
    const resumenHtml = resumenLocales.length > 0
      ? `<h3>Resumen por local</h3>
         <table><thead><tr><th>Local</th><th>Jugadas</th><th>Monto</th></tr></thead>
         <tbody>${resumenRows}</tbody>
         <tfoot><tr><td><strong>Total</strong></td><td>${totalJugadas}</td><td>₡${monto(totalMonto)}</td></tr></tfoot></table><br>` : '';

    const totalesHtml = totalesConsolidado
      ? `<tfoot><tr>
          <td><strong>TOTAL</strong></td>
          <td>₡${monto(totalesConsolidado.monto_vendedor)}</td>
          <td>₡${monto(totalesConsolidado.monto_local)}</td>
          <td>₡${monto(totalesConsolidado.monto_banca)}</td>
          <td>₡${monto(totalesConsolidado.monto_total)}</td>
         </tr></tfoot>`
      : '';

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8">
      <title>Lista ${selectedSorteo?.nombre || ''} — ${fechaFiltro}</title>
      <style>
        body { font-family: monospace; font-size: 12px; margin: 16px; }
        h2 { margin: 0 0 2px 0; font-size: 16px; }
        h3 { margin: 8px 0 4px 0; font-size: 13px; }
        p { margin: 0 0 8px 0; color: #555; font-size: 11px; }
        table { border-collapse: collapse; width: 100%; margin-bottom: 8px; }
        th { background: #f3f4f6; border: 1px solid #ccc; padding: 4px 8px; text-align: left; font-size: 11px; }
        td { border: 1px solid #ccc; padding: 3px 8px; font-size: 11px; }
        tfoot td { font-weight: bold; background: #f9fafb; }
        @media print { @page { margin: 10mm; } }
      </style></head><body>
      <h2>${selectedSorteo?.nombre || ''} (${tipo})</h2>
      <p>Fecha: ${fechaFiltro} &nbsp;|&nbsp; Vista: ${localNombre} &nbsp;|&nbsp; Jugadas: ${printRows.length}</p>
      ${resumenHtml}
      <h3>Lista consolidada</h3>
      <table><thead>${thead}</thead><tbody>${tbody}</tbody>${totalesHtml}</table>
      <script>window.print(); window.close();</script>
    </body></html>`;

    const win = window.open('', '_blank', 'width=900,height=700');
    if (win) {
      win.document.write(html);
      win.document.close();
      console.log('[PRINT Listas] ✓ Web popup lanzado');
    }
    console.log('[PRINT Listas] ─────────────────────────────────');
  };

  const summaryColumns = [
    { key: 'local_nombre', header: 'Local' },
    { key: 'total_vendedores', header: 'Vendedores', render: (r: ResumenLocalRow) => r.total_vendedores },
    { key: 'total_jugadas', header: 'Jugadas', render: (r: ResumenLocalRow) => r.total_jugadas },
    { key: 'total_monto', header: 'Monto', render: (r: ResumenLocalRow) => `₡${monto(r.total_monto)}` },
  ];

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Listas Consolidadas</h1>
        <p className="text-gray-500">Consulta general de ventas por sorteo y por local</p>
      </div>

      <Card>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className="text-sm text-gray-600">Fecha</label>
            <input
              type="date"
              value={fechaFiltro}
              onChange={(e) => setFechaFiltro(e.target.value)}
              className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div>
            <label className="text-sm text-gray-600">Sorteo</label>
            <select
              value={selectedSorteoId}
              onChange={(e) => setSelectedSorteoId(e.target.value)}
              className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg"
            >
              {sorteos.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.nombre} ({s.tipo_id}){(s.jugadas_hoy ?? 0) > 0 ? ` — ${s.jugadas_hoy} jug.` : ''}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-sm text-gray-600">Vista</label>
            <select
              value={selectedLocalId}
              onChange={(e) => setSelectedLocalId(e.target.value)}
              className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="">General (todos los locales)</option>
              {locales.map((loc) => (
                <option key={loc.id} value={loc.id}>{loc.nombre}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-sm text-gray-600">Paquete</label>
            <select
              value={selectedPaqueteId}
              onChange={(e) => setSelectedPaqueteId(e.target.value)}
              className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="">General (sin filtro)</option>
              {paquetes.map((p) => (
                <option key={p.paquete_id} value={p.paquete_id}>
                  {p.paquete_nombre}
                </option>
              ))}
            </select>
          </div>
        </div>
      </Card>

      <Card>
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold text-gray-900">Lista consolidada</h3>
          <div className="flex items-center gap-2">
            <button
              onClick={handleEnviarWa}
              disabled={rows.length === 0 || enviandoWa}
              className="flex items-center gap-1 px-3 py-1.5 text-sm bg-green-50 hover:bg-green-100 disabled:opacity-40 disabled:cursor-not-allowed rounded-lg border border-green-300 text-green-800 transition"
            >
              {enviandoWa ? 'Enviando…' : 'Enviar por WhatsApp'}
            </button>
            <button
              onClick={handlePrint}
              disabled={formattedRows.length === 0}
              className="flex items-center gap-1 px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed rounded-lg border border-gray-300 transition"
            >
              <Printer size={15} />
              Imprimir
            </button>
          </div>
        </div>
        {loadingRows ? (
          <p className="text-sm text-gray-500">Cargando lista...</p>
        ) : (selectedSorteo?.tipo_id === 'NORMAL' || selectedSorteo?.tipo_id === 'REVENTADO') ? (
          <div className="overflow-x-auto">
            <NumerosGrid
              rows={rows}
              tipo={selectedSorteo?.tipo_id as TipoNumerosGrid}
            />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <DynamicListTable
              columns={columnsByTipo(selectedSorteo?.tipo_id || 'NORMAL')}
              rows={formattedRows}
              onSave={async () => { }}
              idField="id"
            />
            {totalesConsolidado && (
              <table className="w-full text-sm mt-1 border-t-2 border-gray-400">
                <tfoot>
                  <tr className="bg-gray-100 font-bold">
                    <td className="px-2 py-2 w-20">TOTAL</td>
                    <td className="px-2 py-2 text-right">₡{monto(totalesConsolidado.monto_vendedor)}</td>
                    <td className="px-2 py-2 text-right">₡{monto(totalesConsolidado.monto_local)}</td>
                    <td className="px-2 py-2 text-right">₡{monto(totalesConsolidado.monto_banca)}</td>
                    <td className="px-2 py-2 text-right">₡{monto(totalesConsolidado.monto_total)}</td>
                  </tr>
                </tfoot>
              </table>
            )}
          </div>
        )}
      </Card>

      <Card>
        <h3 className="font-semibold text-gray-900 mb-2">Resumen por local</h3>
        <div className="overflow-x-auto">
          <DataTable
            columns={summaryColumns as any}
            data={resumenLocales}
            keyExtractor={(r: ResumenLocalRow) => r.local_id}
            isLoading={loadingResumen}
            emptyMessage="Sin movimientos para este sorteo"
          />
        </div>
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card><p className="text-sm text-gray-500">Locales con ventas</p><p className="text-xl font-bold">{resumenLocales.length}</p></Card>
        <Card><p className="text-sm text-gray-500">Vendedores (suma)</p><p className="text-xl font-bold">{totalVendedores}</p></Card>
        <Card><p className="text-sm text-gray-500">Jugadas activas</p><p className="text-xl font-bold">{totalJugadas}</p></Card>
        <Card><p className="text-sm text-gray-500">Monto vendido</p><p className="text-xl font-bold">₡{monto(totalMonto)}</p></Card>
      </div>

      {/* Workflow: listas de locales pendientes de oficializar */}
      {esBanca && selectedSorteoId && (
        <Card>
          <h3 className="font-semibold text-gray-900 mb-3">
            Listas de Locales — Pendientes de Oficializar
          </h3>

          {msgOficializar && (
            <div className={`mb-3 px-3 py-2 rounded text-sm font-medium ${
              msgOficializar.toLowerCase().includes('error')
                ? 'bg-red-50 text-red-800 border border-red-200'
                : 'bg-green-50 text-green-800 border border-green-200'
            }`}>
              {msgOficializar}
            </div>
          )}

          {pendientes.length === 0 ? (
            <p className="text-sm text-gray-500 italic">No hay listas de locales pendientes de oficializar para este sorteo.</p>
          ) : (
            <div className="space-y-2">
              <p className="text-sm text-gray-600 mb-2">
                {pendientes.length} local(es) han enviado listas. Al oficializar se procesan todas juntas.
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="border border-gray-200 px-3 py-2 text-left font-semibold">Local</th>
                      <th className="border border-gray-200 px-3 py-2 text-right font-semibold">Jugadas</th>
                      <th className="border border-gray-200 px-3 py-2 text-left font-semibold">Enviada</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendientes.map((lp) => (
                      <tr key={lp.id} className="hover:bg-gray-50">
                        <td className="border border-gray-200 px-3 py-2 font-medium">{lp.local_nombre}</td>
                        <td className="border border-gray-200 px-3 py-2 text-right">{lp.jugadas_count}</td>
                        <td className="border border-gray-200 px-3 py-2 text-gray-500 text-xs">
                          {lp.enviada_banca_at ? lp.enviada_banca_at.replace('T', ' ').slice(0, 16) : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <button
                onClick={handleOficializar}
                disabled={loadingOficializar}
                className="mt-2 px-5 py-2 text-sm font-semibold bg-green-600 text-white rounded-lg disabled:opacity-40 disabled:cursor-not-allowed hover:bg-green-700 transition"
              >
                {loadingOficializar ? 'Oficializando...' : `Oficializar ${pendientes.length} lista(s)`}
              </button>
            </div>
          )}
        </Card>
      )}
    </div>
  );
};

export default ListasPage;
